import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Sparkles,
    CheckCircle2,
    Monitor,
    Tablet,
    Smartphone,
    MessageSquareWarning,
    ShieldCheck,
    Wallet,
    Loader2,
    Info,
} from "lucide-react";
import api, { errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import { PROFILE_STATUS_STYLES } from "@/lib/constants";
import ProfilePreview from "@/components/ProfilePreview";
import { Textarea } from "@/components/ui/textarea";

function EditableText({ label, value, onSave, multiline = false, testid }) {
    const [text, setText] = useState(value || "");
    const [dirty, setDirty] = useState(false);
    useEffect(() => {
        setText(value || "");
        setDirty(false);
    }, [value]);
    const save = async () => {
        await onSave(text);
        setDirty(false);
    };
    return (
        <div>
            <div className="text-[10px] uppercase tracking-widest text-[#17212B]/50 mb-1">
                {label}
            </div>
            {multiline ? (
                <Textarea
                    rows={5}
                    value={text}
                    onChange={(e) => {
                        setText(e.target.value);
                        setDirty(true);
                    }}
                    data-testid={testid}
                />
            ) : (
                <input
                    className="w-full h-9 px-3 rounded-md border border-[#123B5D]/20"
                    value={text}
                    onChange={(e) => {
                        setText(e.target.value);
                        setDirty(true);
                    }}
                    data-testid={testid}
                />
            )}
            {dirty ? (
                <button
                    className="mt-1 text-xs text-[#0F766E] font-semibold hover:underline"
                    onClick={save}
                    data-testid={`${testid}-save`}
                >
                    Save change
                </button>
            ) : null}
        </div>
    );
}

export default function DoctorFinalReview() {
    const nav = useNavigate();
    const [bundle, setBundle] = useState(null);
    const [viewport, setViewport] = useState("desktop");
    const [busy, setBusy] = useState(false);
    const [comment, setComment] = useState("");
    const [askCorrection, setAskCorrection] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/me/final-review");
            setBundle(data);
        } catch (err) {
            toast.error(errMsg(err, "Failed to load"));
        }
    };
    useEffect(() => {
        load();
    }, []);

    const doctor = bundle?.doctor;
    const status = doctor?.profile_status;
    const canEdit = status === "doctor_review";
    const canApprove = status === "doctor_review";
    const output = bundle?.ai_output?.output || {};

    const saveField = async (field_path, value) => {
        try {
            const { data } = await api.post("/me/final-review/edit", {
                field_path,
                value,
            });
            setBundle((b) => ({
                ...b,
                ai_output: { ...b.ai_output, output: data.output },
            }));
            toast.success("Change saved");
        } catch (err) {
            toast.error(errMsg(err, "Save failed"));
        }
    };
    const approve = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/me/final-review/approve");
            toast.success("Approved — proceed to balance payment.");
            // Create balance order and go to checkout
            try {
                const { data: order } = await api.post("/payments/orders", {
                    package_code: bundle.doctor.package_code,
                });
                nav(`/doctor/checkout/${order.id}`);
            } catch (e) {
                const detail = e?.response?.data?.detail;
                if (detail && typeof detail === "object" && detail.code === "gateway_unavailable") {
                    toast.error("Online payment is being activated", {
                        description: detail.message,
                        duration: 10000,
                    });
                }
                nav("/doctor/packages");
            }
        } catch (err) {
            toast.error(errMsg(err, "Approval failed"));
        } finally {
            setBusy(false);
        }
    };
    const requestCorrection = async () => {
        if (!comment.trim()) {
            toast.error("Add a comment before requesting correction");
            return;
        }
        setBusy(true);
        try {
            await api.post("/me/final-review/request-correction", { comment });
            toast.success("Correction requested — admin will review.");
            setComment("");
            setAskCorrection(false);
            await load();
        } catch (err) {
            toast.error(errMsg(err, "Failed"));
        } finally {
            setBusy(false);
        }
    };

    if (!bundle) {
        return (
            <DoctorShell doctor={null}>
                <div className="text-[#17212B]/50">Loading…</div>
            </DoctorShell>
        );
    }

    const stat = PROFILE_STATUS_STYLES[status];

    // Empty state: nothing to review yet
    if (!bundle.ai_output || !bundle.ai_job || bundle.ai_job.status !== "completed") {
        return (
            <DoctorShell doctor={doctor}>
                <div className="ddi-card p-8 max-w-2xl" data-testid="review-empty">
                    <Info className="text-[#0F766E]" size={20} />
                    <h1 className="mt-2 text-2xl font-bold text-[#123B5D]">
                        Nothing to review yet
                    </h1>
                    <p className="mt-1 text-sm text-[#17212B]/70">
                        {status === "ai_processing"
                            ? "AI is refining your profile — check back shortly."
                            : status === "admin_review"
                            ? "Admin is reviewing your profile. You'll be notified when it's your turn."
                            : "Complete onboarding and submit for AI processing first."}
                    </p>
                </div>
            </DoctorShell>
        );
    }

    return (
        <DoctorShell doctor={doctor}>
            <div className="animate-fade-up">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-[#0F766E] font-semibold">
                            Final Review
                        </div>
                        <h1 className="mt-1 text-3xl font-bold text-[#123B5D]">
                            Your digital identity is ready for your final approval
                        </h1>
                        <div className="mt-2">
                            <span className={"ddi-badge " + (stat?.cls || "")} data-testid="review-status-badge">
                                {stat?.label || status}
                            </span>
                        </div>
                    </div>
                    <div className="inline-flex items-center gap-1 rounded-full bg-[#123B5D]/5 p-1" data-testid="review-viewport-toggle">
                        {[
                            { k: "desktop", i: Monitor, label: "Desktop" },
                            { k: "tablet", i: Tablet, label: "Tablet" },
                            { k: "mobile", i: Smartphone, label: "Mobile" },
                        ].map((v) => {
                            const Icon = v.i;
                            return (
                                <button
                                    key={v.k}
                                    onClick={() => setViewport(v.k)}
                                    className={
                                        "inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-full transition-colors " +
                                        (viewport === v.k
                                            ? "bg-white text-[#123B5D] shadow-sm"
                                            : "text-[#17212B]/60 hover:text-[#123B5D]")
                                    }
                                    data-testid={`review-viewport-${v.k}`}
                                >
                                    <Icon size={12} /> {v.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {canEdit ? (
                    <div className="mt-6 ddi-card p-4 flex items-start gap-3 bg-[#0F766E]/5 border-[#0F766E]/30">
                        <ShieldCheck size={20} className="text-[#0F766E] mt-0.5" />
                        <div className="text-sm text-[#17212B]/80">
                            <div className="font-bold text-[#0F766E]">
                                You can edit any AI-generated text
                            </div>
                            <div>Every change is saved with your name attached. Approve when everything reads correctly.</div>
                        </div>
                    </div>
                ) : null}

                <div className="mt-8 grid grid-cols-1 xl:grid-cols-2 gap-6">
                    <div className="space-y-4" data-testid="review-editor">
                        <div className="ddi-card p-5 space-y-4">
                            <EditableText
                                label="Professional biography"
                                value={output.professional_biography}
                                onSave={(v) => saveField("professional_biography", v)}
                                multiline
                                testid="edit-biography"
                            />
                            <EditableText
                                label="Profile summary"
                                value={output.profile_summary}
                                onSave={(v) => saveField("profile_summary", v)}
                                multiline
                                testid="edit-summary"
                            />
                            <EditableText
                                label="Specialization description"
                                value={output.specialization_description}
                                onSave={(v) => saveField("specialization_description", v)}
                                multiline
                                testid="edit-spec"
                            />
                            <EditableText
                                label="Practice philosophy"
                                value={output.practice_philosophy}
                                onSave={(v) => saveField("practice_philosophy", v)}
                                multiline
                                testid="edit-philosophy"
                            />
                            <EditableText
                                label="Mission & vision"
                                value={output.mission_vision}
                                onSave={(v) => saveField("mission_vision", v)}
                                multiline
                                testid="edit-mission"
                            />
                            <EditableText
                                label="SEO title"
                                value={output.seo_title}
                                onSave={(v) => saveField("seo_title", v)}
                                testid="edit-seo-title"
                            />
                            <EditableText
                                label="Meta description"
                                value={output.meta_description}
                                onSave={(v) => saveField("meta_description", v)}
                                multiline
                                testid="edit-meta"
                            />
                        </div>
                    </div>
                    <div className="min-w-0" data-testid="review-preview">
                        <ProfilePreview
                            doctor={doctor}
                            profile={bundle.profile}
                            output={output}
                            viewport={viewport}
                        />
                    </div>
                </div>

                {canApprove ? (
                    <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        <button
                            className="ddi-teal-btn inline-flex items-center gap-2 disabled:opacity-60"
                            disabled={busy}
                            onClick={approve}
                            data-testid="review-approve-btn"
                        >
                            {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                            Approve & Proceed to Final Payment
                        </button>
                        <button
                            className="text-sm text-[#DC2626] font-semibold inline-flex items-center gap-1"
                            onClick={() => setAskCorrection((v) => !v)}
                            data-testid="review-request-correction-toggle"
                        >
                            <MessageSquareWarning size={14} /> Request correction
                        </button>
                    </div>
                ) : null}

                {askCorrection ? (
                    <div className="mt-4 ddi-card p-4 max-w-2xl" data-testid="review-correction-panel">
                        <div className="text-sm font-semibold text-[#DC2626]">
                            What needs correcting?
                        </div>
                        <Textarea
                            rows={4}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Be specific — the admin will see this exactly."
                            className="mt-2"
                            data-testid="review-correction-comment"
                        />
                        <div className="mt-3 flex gap-2">
                            <button
                                className="ddi-teal-btn text-sm"
                                onClick={requestCorrection}
                                disabled={busy}
                                data-testid="review-correction-submit"
                            >
                                Send to admin
                            </button>
                            <button
                                className="text-sm text-[#17212B]/60"
                                onClick={() => setAskCorrection(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                ) : null}
            </div>
        </DoctorShell>
    );
}
